import React, { useState } from "react";
import { ref, get, update } from "firebase/database";
import { database } from "../../firebase";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../User/ConfirmationDialog"; // your OTP dialog
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [openOtp, setOpenOtp] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Validate password regex
  const validatePassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);

  // Handle OTP confirm from dialog
  const handleOtpConfirm = (email) => {
    setVerifiedEmail(email);
    setOpenOtp(false);
    setError("");
  };

  // Start OTP flow after email input validation
  const handleStartOtp = () => {
    setError("");
    if (!emailInput.endsWith("@tup.edu.ph")) {
      setError("Email must end with @tup.edu.ph");
      return;
    }
    // Save email in localStorage for ConfirmationDialog to read
    localStorage.setItem("currentUser", JSON.stringify({ email: emailInput }));
    setOpenOtp(true);
  };

  // Submit new password without using index query
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validatePassword(newPassword)) {
      setError(
        "Password must be at least 8 characters, include uppercase, lowercase, number, and special character"
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // Get all users
      const usersRef = ref(database, "users");
      const snapshot = await get(usersRef);
      if (!snapshot.exists()) {
        setError("User not found.");
        setLoading(false);
        return;
      }
      const users = snapshot.val();

      // Find user key by email manually
      const userEntry = Object.entries(users).find(
        ([key, user]) => user.email === verifiedEmail
      );

      if (!userEntry) {
        setError("User not found.");
        setLoading(false);
        return;
      }

      const [userKey] = userEntry;

      // Update password field only
      await update(ref(database, `users/${userKey}`), {
        password: newPassword,
      });

      setSuccess("Password updated successfully! Redirecting to login...");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError("Failed to update password: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 400, margin: "40px auto", padding: 20 }}>
      <h2>Forgot Password</h2>
      {!verifiedEmail ? (
        <>
          <TextField
            label="Enter your email"
            fullWidth
            margin="normal"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="your.email@tup.edu.ph"
            disabled={loading}
          />
          {error && (
            <p style={{ color: "red", marginTop: 5 }}>
              {error}
            </p>
          )}
          <Button
            variant="contained"
            onClick={handleStartOtp}
            disabled={loading || !emailInput.trim()}
            sx={{ mt: 2 }}
          >
            Verify Email
          </Button>

          <ConfirmationDialog
            open={openOtp}
            onClose={() => setOpenOtp(false)}
            onConfirm={handleOtpConfirm}
          />
        </>
      ) : (
        <form onSubmit={handlePasswordReset} style={{ marginTop: 20 }}>
          <p>
            Email verified: <b>{verifiedEmail}</b>
          </p>

          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            required
          />
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            required
          />

          {error && (
            <p style={{ color: "red", marginTop: 10 }}>
              {error}
            </p>
          )}

          {success && (
            <p style={{ color: "green", marginTop: 10 }}>
              {success}
            </p>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordPage;
